import { Trans, useTranslation } from 'react-i18next';

import {
	DOCUMENTS_TRIAL_LIMIT,
	DOCUMENT_TTL_HOURS,
	TRIAL_DAYS
} from '@/config';
import { useTrialDiscount } from '@/hooks/user';
import CircleCheck from '@/icons/check-circle.svg';
import { cn } from '@/utils';
import { formatPrice } from '@/utils/money';

function PlansFeatures({ plansData, className }) {
	const { t, i18n } = useTranslation();

	const trialDiscount = useTrialDiscount();

	const features = t('billing.features.table.items', { returnObjects: true });

	const { plans, monthlyPlan, trialPlan } = plansData;

	const featureValues = t('billing.features.table.values', {
		returnObjects: true
	});

	return (
		<div
			className={cn(
				'relative mt-[4.25rem] flex w-full flex-col pb-[60px]',
				className
			)}
		>
			<h2 className="pb-10 font-title text-[32px] font-semibold leading-10">
				{t('billing.features.title')}
			</h2>

			<table className="bg-white">
				<thead>
					<tr>
						<th className="block w-full border border-grey-200 px-4 py-2 text-left text-xs font-semibold leading-7 md:text-base">
							{t('billing.features.table.title')}
						</th>
					</tr>
				</thead>

				<tbody>
					{features.map((feat, index) => (
						<tr
							className="block border-b border-grey-200"
							key={`tr-feat-${index}`}
						>
							<th className="inline-block w-full border-x border-grey-200 p-4 text-left text-xs font-semibold leading-5 text-grey-500">
								<div className="flex flex-col gap-3">
									<span className="text-sm font-normal text-black">
										{feat}
									</span>

									<div className="flex flex-col gap-2">
										{plans.map((plan, ix) => {
											const valueText =
												featureValues[plan.name]?.[index];

											const ariaLabel = valueText
												? undefined
												: t('common.yes');

											const limitedValues = valueText ? (
												<Trans
													defaults={valueText}
													components={{
														strong: (
															<strong className="font-semibold" />
														)
													}}
													values={{
														trialPrice: formatPrice({
															amount: trialDiscount
																? 0
																: trialPlan?.baseAmount,
															currency: trialPlan?.currency
														}),
														monthlyPrice: formatPrice({
															amount: monthlyPlan?.baseAmount,
															currency: monthlyPlan?.currency
														}),
														trialDays: TRIAL_DAYS,
														dayLimit: DOCUMENTS_TRIAL_LIMIT,
														documentTtlHours: DOCUMENT_TTL_HOURS
													}}
												/>
											) : (
												<CircleCheck
													aria-hidden
													className="m-auto text-black"
												/>
											);

											return (
												<div
													aria-label={ariaLabel}
													key={plan.name + ix}
													className={cn(
														'flex',
														valueText
															? 'flex-col'
															: 'justify-between'
													)}
												>
													<div className="flex items-center gap-2">
														<span className="size-1 rounded-full bg-red-500" />
														<p className="text-black">
															{i18n.exists(
																`plans.${plan.name}.featureName`
															)
																? t(
																		`plans.${plan.name}.featureName`
																	)
																: t(`plans.${plan.name}.name`)}
														</p>
													</div>

													<span className="ml-3 block max-w-[263px] font-normal">
														{limitedValues}
													</span>
												</div>
											);
										})}
									</div>
								</div>
							</th>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export default PlansFeatures;
